import React, { useEffect, useState } from 'react';
import { GIButton, Modal } from 'app-components';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ModalContextType } from '../../../../contexts/modalContext';
import useApiHandling from '../../hooks/useApiHandling';
import {
    MaskedInput,
    ValidationErrorMessage,
    IGetSepDetails,
    CsrOptionName,
    checkCurrentYear,
    DATE_MASK,
    COMPLETE_INPUT
} from '../../utils';
import { isDateValid } from '../../../../utils/dates/date.utils';
import { MM_SLASH_DD_SLASH_YYYY } from '../../../../utils/dates/dateFormats.utils';

const StyledField = styled.div`
    .date-picker,
    .shown.error--msg {
        margin-top: 4px !important;
        padding: 8px;
        background-color: ${({ theme }) => theme.dangerLightest};
    }
    @media (max-width: 768px) {
        flex-direction: column;
        justify-content: center;
    }

    .gi-input {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        text-align: left;
    }

    .gi-input__label {
        margin-left: -10px;
    }

    @media (max-width: 768px) {
        .gi-input__label {
            margin-left: 0px;
        }
    }
`;

const StyledModalBody = styled.div`
    .gi-input__input {
        width: 130px;
    }

    @media (max-width: 768px) {
        .gi-modal__body {
            max-height: 0%;
            padding-bottom: 20px;
        }
    }
`;

const StyledControls = styled.div`
    display: flex;
    gap: 10px;
    justify-content: flex-end;

    @media (max-width: 768px) {
        margin-top: 0;
        flex-direction: column;
        gap: 5px;
    }
`;

interface IModalProps extends ModalContextType {
    sepDetails: IGetSepDetails;
    coverageYear: number;
}

const ChangeCoverageStartDateModal = ({ closeModal, sepDetails, coverageYear }: IModalProps) => {
    const [newSEPEndDate, setNewSEPEndDate] = useState(sepDetails.covStartDate);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [continueDisabled, setContinueDisabled] = useState(true);
    const { handleSaveSepDetails } = useApiHandling();

    useEffect(() => {
        const isValidDate = isDateValid(newSEPEndDate, MM_SLASH_DD_SLASH_YYYY);
        const isCurrentYearValid = checkCurrentYear(newSEPEndDate, coverageYear);
        setContinueDisabled(!(isValidDate && isCurrentYearValid));
    }, [newSEPEndDate]);

    const handleCovStartdateCheck = (date: string) => {
        const isValidDate = isDateValid(date, MM_SLASH_DD_SLASH_YYYY);
        const isCurrentYearValid = checkCurrentYear(date, coverageYear);
        const isFilledInput = COMPLETE_INPUT.test(date);
        if (!(isValidDate && isCurrentYearValid) && !isFilledInput && date) {
            setErrorMsg('cap.applications.modal.csrspedateerror');
        } else {
            setErrorMsg(null);
        }
    };

    function onClose() {
        closeModal('changeCoverageStartDateModal');
    }

    function onContinue() {
        const updatedSepDetails = {
            ...sepDetails,
            covStartDate: newSEPEndDate,
        };
        handleSaveSepDetails(updatedSepDetails, CsrOptionName.COVERAGE_DATE);
    }

    return (
        <Modal id="override_sep_qep_denial_modal">
            <Modal.Header
                id="change_coverage_start_date_modal_title"
                titleId="cap.applications.modal.coverageDate.title"
                onHide={onClose}
            />
            <StyledModalBody>
                <Modal.Body>
                    <FormattedMessage id="cap.application.modal.csrcovstartinfo" />
                    <StyledField>
                        <MaskedInput
                            label={{
                                labelText: 'cap.application.modal.csrentercovstart',
                                labelClasses: [''],
                                wrapperClasses: [''],
                                isRequired: true,
                            }}
                            input={{
                                id: 'change_coverage_start_date',
                                name: 'newSEPEndDate',
                                placeholder: 'mm/dd/yyyy',
                                type: 'text',
                                isInputRequired: true,
                                value: newSEPEndDate,
                                mask: DATE_MASK,
                            }}
                            actions={{
                                handleFieldChange: (date: string) => {
                                    setNewSEPEndDate(date);
                                    handleCovStartdateCheck(date);
                                },
                                handleFieldBlur: (date: string) => handleCovStartdateCheck(date),
                            }}
                        />
                        <ValidationErrorMessage isVisible={errorMsg !== null} messageText={errorMsg} />
                    </StyledField>
                </Modal.Body>
            </StyledModalBody>
            <Modal.Controls>
                <StyledControls>
                    <GIButton
                        id="override_sep_qep_denial_modal_continue_button"
                        onClickHandler={onContinue}
                        isDisabled={continueDisabled}
                        variant="primary"
                        labelId="button.continue"
                    />
                    <GIButton 
                        id="override_sep_qep_denial_modal_cancel_button" 
                        onClickHandler={onClose} 
                        variant="secondary" 
                        labelId="button.cancel" 
                    />
                </StyledControls>
            </Modal.Controls>
        </Modal>
    );
};

export default ChangeCoverageStartDateModal;
